import React, { useRef, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { TranscriptContainer, LiveTranscriptSection, CommitteeSummary } from '../../components/videos/index';
import { useLiveInfo, useTranscript, useVideoPlayer } from '../../hooks/streaming';
import TabsSwitch from '../../components/Structures/TabsSwitch';

const LIVE_URL = process.env.REACT_APP_LIVES_SERVER_URL || 'https://7zfimj8vbrgpdu-8080.proxy.runpod.net/';

function Streaming() {
  const { liveId, state } = useParams();
  const [searchParams] = useSearchParams();
  const timestampParam = searchParams.get('timestamp');

  const videoRef = useRef(null);

  const { info, loadingInfo } = useLiveInfo(liveId, state);

  const isOnLive = info?.IsOnLive;

  const hlsStreamUrl = isOnLive ? `${LIVE_URL}hls/${liveId}/index.m3u8` : info?.HlsUrl;
  useVideoPlayer(videoRef, hlsStreamUrl, isOnLive);
  const { transcripts, loadingTranscript } = useTranscript(liveId, isOnLive, info);

  const [showOption, setShowOption] = useState('transcript');

  const onGoToTimeStamp = (timeInSeconds) => {
    if (videoRef.current && timeInSeconds) {
      videoRef.current.currentTime = timeInSeconds;
      videoRef.current.play();
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      if (timestampParam) {
        video.currentTime = Number(timestampParam);
      } else if (!isOnLive && video.duration) {
        video.currentTime = Math.max(0, video.duration - 10);
      }
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [timestampParam, isOnLive, info]);

  const renderMainContent = () => {
    switch (showOption) {
      case 'transcript':
        return isOnLive ? (
          <LiveTranscriptSection
            transcripts={transcripts}
            loadingInfo={loadingInfo}
            loadingTranscript={loadingTranscript}
            onGoToTimeStamp={onGoToTimeStamp}
            initialTimestamp={timestampParam ? Number(timestampParam) : null}
          />
        ) : (
          <TranscriptContainer onGoToTimeStamp={onGoToTimeStamp} transcript={transcripts} />
        );
      case 'key-points':
        return <CommitteeSummary onTimeSelect={onGoToTimeStamp} summaries={info?.Summary} />;
      case 'summary':
        return (
          <div className="h-full max-h-screen flex-1 flex flex-col py-[15px] px-2.5 sm:px-5 border border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
              <h2
                id="Office-Information"
                className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8"
              >
                Committee Summary
              </h2>
            </div>
            <div className="flex flex-col gap-5 px-6 py-3 border border-[#E5E7EB] bg-white overflow-y-auto overflow-x-hidden h-[calc(100vh-260px)] whitespace-pre-wrap">
              {info?.NonTimestampSummary}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full flex gap-4">
      <div className="w-[40%] h-full overflow-hidden">
        <div className="py-[15px] px-5 border border-[#E5E7EB] bg-white mb-4">
          <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
            <span className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8">
              Recording: <strong>{info?.Title || 'Loading...'}</strong>
            </span>
          </div>
          <div className="w-full flex justify-center">
            <video ref={videoRef} controls autoPlay style={{ width: '100%' }} />
          </div>
        </div>
      </div>

      <div className="flex flex-col h-full max-h-screen w-[60%]">
        {!isOnLive && info?.Summary && (
          <TabsSwitch
            options={[
              { label: 'Committee Transcript', value: 'transcript' },
              { label: 'Key Points', value: 'key-points' },
              { label: 'Summary', value: 'summary' },
            ]}
            isActive={showOption}
            onSwitch={setShowOption}
          />
        )}

        {renderMainContent()}
      </div>
    </div>
  );
}

export default Streaming;
